import {
  Button,
  FormMessage,
  FormMessageVariant,
  Input,
  InputGroup,
  Placement,
  type SelectOption,
  toast,
} from '@aus-platform/design-system';
import { type ImageryLayer } from 'cesium';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
} from 'src/map-3d/shared/map-3d-slices';
import {
  type ComplianceAssessmentPayload,
  FeatureGeometryType,
  FeatureType,
  handleResponseSuccessMessage,
  useAddComplianceAssessmentRequest,
  useIterationsList,
} from 'src/shared/api';
import { useInputFields } from 'src/shared/hooks';
import {
  type WorkspaceLayer,
  WorkspaceLayerErrorMessage,
  type WorkspaceLayerListObj,
} from '../../../shared';
import { setVectorLayerVisibility } from '../../map-3d-workspace';
import { complianceAssessmentValidator } from './compliance-assessment-validator';
import { complianceAssessmentInitialState } from './constants';
import { WorkspaceZoneType } from './enums';
import {
  addMissingLayersToCesium,
  removeUnselectedLayersFromCesium,
} from './helpers';
import {
  type ComplianceAssessmentType,
  type WorkspaceLayersStatus,
} from './types';

// Constants.
const MAX_MENU_HEIGHT = 182;

export const ComplianceAssessmentGenerateForm: React.FC = () => {
  // useStates.
  const [iterationList, setIterationList] = useState<SelectOption[]>([]);
  const [workspaceLayersStatus, setWorkspaceLayersStatus] =
    useState<WorkspaceLayersStatus>({
      hasActivePolygons: true,
      hasActiveLines: true,
    });

  // Refs.
  const excavationLayersRef = useRef<Set<string>>(new Set());
  const dumpLayersRef = useRef<Set<string>>(new Set());
  const sectionLineLayersRef = useRef<Set<string>>(new Set());

  // Selectors.
  const { selectedTerrainIteration, selectedTerrainSite } =
    useAppSelector(selectMap3dDataset);

  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);

  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);

  const activeLineLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(state, FeatureGeometryType.Line),
  );

  const activePolygonLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  const polygonLayerOptions: SelectOption<WorkspaceLayer>[] =
    activePolygonLayers.map((activeLayer: WorkspaceLayer) => ({
      label: activeLayer.name,
      value: activeLayer,
    }));

  const lineLayerOptions: SelectOption<WorkspaceLayer>[] = activeLineLayers.map(
    (activeLayer: WorkspaceLayer) => ({
      label: activeLayer.name,
      value: activeLayer,
    }),
  );

  // Hooks.
  const {
    values,
    setValues,
    setErrors,
    errors,
    names,
    onChange,
    onBlur,
    onFocus,
  } = useInputFields<ComplianceAssessmentType>(
    complianceAssessmentInitialState,
    complianceAssessmentValidator,
  );

  const {
    comparisonIteration,
    excavationZoneLayers,
    dumpZoneLayers,
    sectionLineLayers,
    plannedDumpQuantity,
    plannedExcavationQuantity,
  } = values;

  // useMemos.
  /**
   * Convert the workspaceLayers object to an array of [key, value] pairs,
   * filter out the layers where `layer.show` is false,
   * then convert the filtered array back to an object.
   */
  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(() => {
    return Object.fromEntries(
      Object.entries(workspaceLayers).filter(([, layer]) => layer.show),
    );
  }, [workspaceLayers]);

  // Api.
  const {
    data: iterationListResponse,
    isLoading: isLoadingIterationList,
    isSuccess: isSuccessIterationList,
  } = useIterationsList({ siteId: selectedTerrainSite?.id ?? '' });

  const {
    mutate: sendComplianceAssessmentRequest,
    data: generateRequestResponse,
    isSuccess: isSuccessGenerateRequest,
    isPending: isLoadingGenerateRequest,
    isError: isErrorGenerateRequest,
    error: generateRequestError,
  } = useAddComplianceAssessmentRequest();

  // useEffects.
  useEffect(() => {
    setVectorLayerVisibility(visibleWorkspaceLayers, false);

    setWorkspaceLayersStatus({
      hasActivePolygons: !isEmpty(activePolygonLayers),
      hasActiveLines: !isEmpty(activeLineLayers),
    });
  }, [visibleWorkspaceLayers]);

  useEffect(() => {
    if (isSuccessGenerateRequest) {
      setValues(complianceAssessmentInitialState);
      removeAllImageryLayers();
      clearAllLayerRefs();

      handleResponseSuccessMessage(
        isSuccessGenerateRequest,
        generateRequestResponse,
      );
    }

    if (isErrorGenerateRequest && generateRequestError) {
      toast.error('Failed to generate request.');
    }
  }, [
    isSuccessGenerateRequest,
    generateRequestResponse,
    isErrorGenerateRequest,
    generateRequestError,
  ]);

  useEffect(() => {
    return () => {
      removeAllImageryLayers();
      clearAllLayerRefs();
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    if (isSuccessIterationList && iterationListResponse) {
      setIterationList(
        iterationListResponse.list
          .filter(({ id }) => id !== selectedTerrainIteration?.id)
          .map(({ id, name }) => ({ label: name, value: id })),
      );
    }
  }, [isSuccessIterationList, iterationListResponse]);

  useEffect(() => {
    if (!cesiumProxy || !excavationZoneLayers) {
      return;
    }

    const selectedIds = new Set(
      excavationZoneLayers.map(({ value }) => value.id),
    );

    removeUnselectedLayersFromCesium(
      WorkspaceZoneType.Excavation,
      selectedIds,
      excavationLayersRef.current,
      cesiumProxy,
      aoiManager,
    );

    addMissingLayersToCesium(
      WorkspaceZoneType.Excavation,
      excavationZoneLayers,
      excavationLayersRef.current,
      cesiumProxy,
      aoiManager,
    );
  }, [excavationZoneLayers]);

  useEffect(() => {
    if (!cesiumProxy || !dumpZoneLayers) {
      return;
    }

    const selectedIds = new Set(dumpZoneLayers.map(({ value }) => value.id));

    removeUnselectedLayersFromCesium(
      WorkspaceZoneType.Dump,
      selectedIds,
      dumpLayersRef.current,
      cesiumProxy,
      aoiManager,
    );

    addMissingLayersToCesium(
      WorkspaceZoneType.Dump,
      dumpZoneLayers,
      dumpLayersRef.current,
      cesiumProxy,
      aoiManager,
    );
  }, [dumpZoneLayers]);

  useEffect(() => {
    if (!cesiumProxy || !sectionLineLayers) {
      return;
    }

    const selectedIds = new Set(sectionLineLayers.map(({ value }) => value.id));

    removeUnselectedLayersFromCesium(
      WorkspaceZoneType.SectionLine,
      selectedIds,
      sectionLineLayersRef.current,
      cesiumProxy,
      aoiManager,
    );

    addMissingLayersToCesium(
      WorkspaceZoneType.SectionLine,
      sectionLineLayers,
      sectionLineLayersRef.current,
      cesiumProxy,
      aoiManager,
      FeatureType.LineString,
    );
  }, [sectionLineLayers]);

  // Variables.
  const isGenerateRequestEnabled =
    comparisonIteration &&
    !isEmpty(excavationZoneLayers) &&
    !isEmpty(dumpZoneLayers) &&
    !isEmpty(sectionLineLayers) &&
    !errors.plannedDumpQuantity &&
    !errors.plannedExcavationQuantity;

  // Handlers.
  const handleGenerateRequestSubmit = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (
      !selectedTerrainSite ||
      !selectedTerrainIteration ||
      !comparisonIteration ||
      isNil(excavationZoneLayers) ||
      isNil(dumpZoneLayers) ||
      isNil(sectionLineLayers) ||
      errors.plannedDumpQuantity ||
      errors.plannedExcavationQuantity
    ) {
      return;
    }

    const ComplianceAssessmentPayload: ComplianceAssessmentPayload = {
      site: selectedTerrainSite.id,
      baseIteration: selectedTerrainIteration.id,
      comparisonIteration: comparisonIteration.value,
      plannedExcavationZones: excavationZoneLayers.map(({ value }) => value.id),
      plannedDumpZones: dumpZoneLayers.map(({ value }) => value.id),
      sectionLines: sectionLineLayers.map(({ value }) => value.id),
    };

    if (!isNil(plannedExcavationQuantity)) {
      ComplianceAssessmentPayload.plannedExcavationQuantity =
        plannedExcavationQuantity;
    }

    if (!isNil(plannedDumpQuantity)) {
      ComplianceAssessmentPayload.plannedDumpQuantity = plannedDumpQuantity;
    }

    sendComplianceAssessmentRequest(ComplianceAssessmentPayload);
  };

  const handleSelectChange = (
    selectedOption: SelectOption<WorkspaceLayer>,
    fieldName: string,
  ) => {
    setValues((values) => ({
      ...values,
      [fieldName]: selectedOption,
    }));

    setErrors((errors) => ({
      ...errors,
      [fieldName]: '',
    }));
  };

  const removeAllImageryLayers = () => {
    const imageryLayers = cesiumProxy?.layerManager.getImageryLayers();

    if (isNil(imageryLayers)) {
      return;
    }

    const layersToRemove: ImageryLayer[] = [];

    for (let index = 0; index < imageryLayers.length; index++) {
      const layer = imageryLayers.get(index);

      if (layer['layerId']) {
        layersToRemove.push(layer);
      }
    }

    if (!isEmpty(layersToRemove)) {
      layersToRemove.forEach((layer) => {
        aoiManager?.removeImageryLayer(layer);
      });
      cesiumProxy?.layerManager?.removeImageryLayers(layersToRemove);
    }
  };

  const clearAllLayerRefs = () => {
    excavationLayersRef.current.clear();
    dumpLayersRef.current.clear();
    sectionLineLayersRef.current.clear();
  };

  // Renders.
  const renderFormErrorMessage = (message: string) => (
    <FormMessage
      className="compliance-assessment__form-error-message"
      message={message}
      variant={FormMessageVariant.Error}
    />
  );

  return (
    <div className="compliance-assessment">
      <form
        className="compliance-assessment__form"
        onSubmit={handleGenerateRequestSubmit}
      >
        <div className="compliance-assessment__heading">Active Dataset</div>
        <InputGroup>
          <Input.Label>Site </Input.Label>
          <Input.Text value={selectedTerrainSite?.name} disabled />
        </InputGroup>

        <InputGroup>
          <Input.Label>Iteration </Input.Label>
          <Input.Text value={selectedTerrainIteration?.name} disabled />
        </InputGroup>

        <InputGroup className="compliance-assessment__comparison-iteration">
          <Input.Label
            info="Select a comparison iteration that is newer than the active iteration to prevent incorrect excavation and dump outputs."
            infoPlacement={Placement.Top}
            tooltipClassname="select-iteration-tooltip"
          >
            Select Iteration to compare
          </Input.Label>
          {isEmpty(iterationList) && !isLoadingIterationList ? (
            <div className="compliance-assessment__comparison-iteration__empty">
              No Iterations to compare.
            </div>
          ) : (
            <Input.Select
              placeholder="Select Iteration"
              options={iterationList}
              isLoading={isLoadingIterationList}
              isDisabled={isLoadingGenerateRequest}
              value={values.comparisonIteration}
              name={names.comparisonIteration}
              error={errors.comparisonIteration}
              onChange={(selectedOption: SelectOption<WorkspaceLayer>) =>
                handleSelectChange(selectedOption, names.comparisonIteration)
              }
              {...{ onBlur, onFocus }}
            />
          )}
        </InputGroup>

        {!isEmpty(iterationList) && (
          <FormMessage
            className="compliance-assessment__form-message"
            message="The comparison iteration is subtracted from the active iteration to detect changes over time."
            variant={FormMessageVariant.Info}
          />
        )}

        <div className="compliance-assessment__heading">
          Select Layers from Workspace
        </div>

        <InputGroup>
          <Input.Label>Planned Excavation Zones</Input.Label>
          <Input.Select
            placeholder="Select Layer(s) from Workspace"
            options={polygonLayerOptions}
            value={values.excavationZoneLayers}
            name={names.excavationZoneLayers}
            error={errors.excavationZoneLayers}
            isDisabled={
              isEmpty(polygonLayerOptions) || isLoadingGenerateRequest
            }
            isMulti
            maxMenuHeight={MAX_MENU_HEIGHT}
            onChange={(selectedOption: SelectOption<WorkspaceLayer>) => {
              handleSelectChange(selectedOption, names.excavationZoneLayers);
            }}
            {...{ onBlur, onFocus }}
          />
        </InputGroup>
        {!workspaceLayersStatus.hasActivePolygons &&
          renderFormErrorMessage(WorkspaceLayerErrorMessage.Polygon)}

        <InputGroup>
          <Input.Label>Planned Dump Zones</Input.Label>
          <Input.Select
            placeholder="Select Layer(s) from Workspace"
            options={polygonLayerOptions}
            value={values.dumpZoneLayers}
            name={names.dumpZoneLayers}
            error={errors.dumpZoneLayers}
            isDisabled={
              isEmpty(polygonLayerOptions) || isLoadingGenerateRequest
            }
            isMulti
            maxMenuHeight={MAX_MENU_HEIGHT}
            onChange={(selectedOption: SelectOption<WorkspaceLayer>) =>
              handleSelectChange(selectedOption, names.dumpZoneLayers)
            }
            {...{ onBlur, onFocus }}
          />
        </InputGroup>
        {!workspaceLayersStatus.hasActivePolygons &&
          renderFormErrorMessage(WorkspaceLayerErrorMessage.Polygon)}

        <InputGroup>
          <Input.Label>Section Lines</Input.Label>
          <Input.Select
            placeholder="Select Layer(s) from Workspace"
            options={lineLayerOptions}
            value={values.sectionLineLayers}
            name={names.sectionLineLayers}
            error={errors.sectionLineLayers}
            onChange={(selectedOption: SelectOption<WorkspaceLayer>) =>
              handleSelectChange(selectedOption, names.sectionLineLayers)
            }
            isDisabled={isEmpty(lineLayerOptions) || isLoadingGenerateRequest}
            isMulti
            maxMenuHeight={MAX_MENU_HEIGHT}
            menuPlacement={
              workspaceLayersStatus.hasActivePolygons
                ? Placement.Bottom
                : Placement.Top
            }
            {...{ onBlur, onFocus }}
          />
        </InputGroup>
        {!workspaceLayersStatus.hasActiveLines &&
          renderFormErrorMessage(WorkspaceLayerErrorMessage.Line)}

        <div className="compliance-assessment__title">Input Quantity</div>

        <InputGroup>
          <Input.Label>
            Planned Excavation Quantity{' '}
            <span className="faded-label">
              (m<sup>3</sup>)
            </span>
          </Input.Label>

          <Input.Text
            value={values.plannedExcavationQuantity || ''}
            name={names.plannedExcavationQuantity}
            placeholder="Planned Excavation Quantity"
            error={errors.plannedExcavationQuantity}
            isInvalid={!!errors.plannedExcavationQuantity}
            {...{ onChange, onBlur, onFocus }}
          ></Input.Text>
        </InputGroup>

        <InputGroup>
          <Input.Label>
            Planned Dump Quantity{' '}
            <span className="faded-label">
              (m<sup>3</sup>)
            </span>
          </Input.Label>

          <Input.Text
            value={values.plannedDumpQuantity || ''}
            name={names.plannedDumpQuantity}
            placeholder="Planned Dump Quantity"
            error={errors.plannedDumpQuantity}
            isInvalid={!!errors.plannedDumpQuantity}
            {...{ onChange, onBlur, onFocus }}
          ></Input.Text>
        </InputGroup>

        <div className="compliance-assessment__footer">
          <Button
            disabled={!isGenerateRequestEnabled}
            type="submit"
            isLoading={isLoadingGenerateRequest}
          >
            {isLoadingGenerateRequest
              ? 'Generating Request'
              : 'Generate Request'}
          </Button>
        </div>
      </form>
    </div>
  );
};
